import type { MouseEvent } from 'react'
import { legacyContractDataAttrs } from '@/msgdeskRuntime/legacyBridge/SelectorContracts'
import { dispatchSubsSelection } from '@/msgdeskRuntime/subsSelection'
import {
  actionContextForDeskMode,
  dispatchCheckedItemAction,
  itemActionsForContext,
  type ItemActionContext,
  type ItemActionId,
} from '@/msgdeskRuntime/itemMovements'
import { selectCheckedSubIds, selectFavsAllowed } from '@/msgdeskRuntime/state'
import { useSubsStore } from '@/state/MsgTreeProvider'

// Modern checked-info bundle — self-contained (markup + wiring + Tailwind).
// Same behaviour/data as ../Legacy; re-skinned via md-checked-info--modern:
// a checkbox-style select-all, badge counters, and pill action links.

type CheckedsMode = 'msg' | 'trash' | 'drafts' | 'favs'

const actionLabels: Readonly<Record<ItemActionContext, Partial<Record<ItemActionId, string>>>> = {
  active: {
    'move-to-trash': 'Delete',
    'move-to-drafts': 'In Drafts',
    'add-favorite': 'Fav',
  },
  drafts: {
    'move-to-trash': 'Delete',
    'restore-to-active': 'Create',
  },
  trash: {
    'restore-to-active': 'Restore',
    // Legacy trash-from-trash move = PERMANENT delete (m11 is_removing).
    // The contract always allowed it; the label was missing, leaving purge
    // unreachable from the UI (audit 2026-07-19 D4).
    purge: 'Delete forever',
  },
  favorites: {
    'remove-favorite': 'Unfav',
  },
}

export function CheckedsInfo({
  section,
  mode = 'msg',
  visual = 'modern',
}: {
  section: 0 | 1
  mode?: CheckedsMode
  visual?: 'modern' | 'legacy'
}) {
  const { state } = useSubsStore()
  const actionContext = actionContextForDeskMode(mode)
  // FAVS-IDENTITY: bulk favorite actions hide with the rest of the favs UI.
  const favsAllowed = selectFavsAllowed(state)
  const actions = itemActionsForContext(actionContext).flatMap(action => {
    if (!favsAllowed && (action === 'add-favorite' || action === 'remove-favorite')) return []
    const label = actionLabels[actionContext][action]
    return label ? [{ action, label }] : []
  })
  const contract = section === 0 ? 'subs.checked.answers' : 'subs.checked.submsges'
  const sectionKey = section === 0 ? 'answers' : 'submsges'
  const currentIds = state.renderProjection.rootIds[sectionKey] || []
  // DATA: checked ids (read-only) derived from the store projection.
  const checkedMode = state.renderProjection.mode === 'subs' ? sectionKey : state.renderProjection.mode
  const checkedIds = selectCheckedSubIds(state, sectionKey, checkedMode)
  const checkedSet = new Set(checkedIds)
  const currentCheckedCount = currentIds.filter(id => checkedSet.has(id)).length
  const allCheckedCount = checkedSet.size
  const hasChecked = allCheckedCount > 0
  const isLegacy = visual === 'legacy'
  const actionClass = isLegacy
    ? `md-checked-info__action${hasChecked ? ' md-checked-info__action--visible inline-flex' : ' hidden'} ml-2 mt-0.5 cursor-pointer items-center text-[11px] text-black underline`
    : `md-checked-info__action${hasChecked ? ' md-checked-info__action--visible inline-flex' : ' hidden'} ml-2 items-center rounded-md bg-[#2563eb1a] px-[9px] py-[3px] text-[11px] font-semibold text-[#2563eb] no-underline transition-colors duration-150 hover:bg-[#2563eb33]`

  // BEHAVIOUR: toggle "check all visible" for this section (legacy checkVisibleSubs).
  function handleCheckVisible(event: MouseEvent<HTMLElement>) {
    dispatchSubsSelection({ action: 'toggle-visible', section, target: event.currentTarget })
  }

  function handleAction(event: MouseEvent<HTMLElement>, action: ItemActionId) {
    // Purge is irreversible (the server deletes the row and its subtree) —
    // one confirm guards against a mis-click. All other actions are
    // reversible moves and stay one-click, as before.
    if (action === 'purge' && !window.confirm('Delete the selected items forever? This cannot be undone.')) return
    void dispatchCheckedItemAction({
      action,
      context: actionContext,
      section,
      target: event.currentTarget,
    })
  }

  return (
    <div
      {...legacyContractDataAttrs(contract)}
      className={isLegacy
        ? `md-checked-info md-checked-info--legacy relative z-[2] m-0 min-h-4 items-start bg-transparent p-0 text-[10px] leading-none text-black font-[Arial,Tahoma,Verdana,sans-serif] ${hasChecked ? 'flex' : 'hidden'}`
        : 'md-checked-info md-checked-info--modern relative z-[2] m-0 inline-flex min-h-[18px] items-center gap-1.5 bg-transparent p-0 text-xs leading-none text-slate-900 font-[Arial,Tahoma,Verdana,sans-serif]'}
    >
      <span
        className={isLegacy
          ? 'md-checked-info__flag m-0 flex h-4 w-4 flex-[0_0_16px] cursor-pointer items-center justify-center text-[14px] leading-none text-[#3472a9]'
          : 'md-checked-info__flag m-0 inline-flex h-[18px] w-[18px] flex-[0_0_18px] cursor-pointer items-center justify-center rounded-[5px] border-[1.5px] border-slate-300 bg-white text-[11px] leading-none text-slate-400 transition-colors duration-150 hover:border-[#2563eb] hover:text-[#2563eb]'}
        title="Select all"
        onClick={handleCheckVisible}
      >
        {isLegacy ? '⚑' : '✓'}
      </span>
      <span className={isLegacy ? 'md-checked-info__current mt-0.5 text-[10px] text-black' : 'md-checked-info__current m-0 text-xs font-bold text-slate-900'}>{hasChecked && currentCheckedCount > 0 ? currentCheckedCount : ''}</span>
      <span className={isLegacy ? 'md-checked-info__all ml-0.5 mt-0.5 text-[10px] text-[#a9a9a9]' : 'md-checked-info__all ml-0.5 text-xs text-slate-400'}>{hasChecked ? `(${allCheckedCount})` : ''}</span>
      {actions.map(({ action, label }) => (
        <span
          key={action}
          className={actionClass}
          data-md-item-action={action}
          onClick={event => handleAction(event, action)}
        >
          {label}
        </span>
      ))}
    </div>
  )
}
